import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { EVENT_TYPES } from "@/lib/quote";

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border p-6 sm:p-8 shadow-sm">
    <h2 className="font-display text-2xl font-bold text-[#2C1A11]">{title}</h2>
    <p className="text-sm text-[#8a7a6d] mb-6">{subtitle}</p>
    <div className="space-y-5">{children}</div>
  </div>
);

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-semibold text-[#2C1A11]">{label}</Label>
    {children}
  </div>
);

export const InfoSteps = ({ step, data, update, config = {}, extraHours = 0, guestsCount = 0, animMandatory = false, animPrice = 120 }) => {
  if (step === 0)
    return (
      <Card title="I tuoi contatti" subtitle="Come possiamo ricontattarti per il tuo evento">
        <F label="Nome e cognome *">
          <Input data-testid="contact-name-input" value={data.contact.name} onChange={(e) => update("contact", "name", e.target.value)} placeholder="Mario Rossi" />
        </F>
        <div className="grid sm:grid-cols-2 gap-5">
          <F label="Telefono *">
            <Input data-testid="contact-phone-input" type="tel" value={data.contact.phone} onChange={(e) => update("contact", "phone", e.target.value)} placeholder="333 1234567" />
          </F>
          <F label="Email">
            <Input data-testid="contact-email-input" type="email" value={data.contact.email} onChange={(e) => update("contact", "email", e.target.value)} placeholder="mario@email.it" />
          </F>
        </div>
        <F label="Note / richieste particolari">
          <Textarea data-testid="contact-notes-input" value={data.contact.notes} onChange={(e) => update("contact", "notes", e.target.value)} placeholder="Es. tema della festa, preferenze..." />
        </F>
      </Card>
    );

  if (step === 1)
    return (
      <Card title="Il tuo evento" subtitle="Tipo di evento, data e orari">
        <F label="Tipo di evento *">
          <div className="flex flex-wrap gap-2">
            {EVENT_TYPES.map((t) => (
              <button key={t} data-testid={`event-type-${t.replace(/[^a-zA-Z]/g, "")}`} onClick={() => update("event", "type", t)}
                className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-colors ${data.event.type === t ? "bg-[#C85A32] text-white border-[#C85A32]" : "bg-white text-[#2C1A11] hover:border-[#C85A32]"}`}>
                {t}
              </button>
            ))}
          </div>
        </F>
        {data.event.type === "Altro" && (
          <F label="Descrivi il tuo evento *">
            <Input data-testid="event-type-custom-input" value={data.event.type_custom || ""} onChange={(e) => update("event", "type_custom", e.target.value)} placeholder="Es. Baby shower, festa di pensionamento, cena di classe..." />
          </F>
        )}
        <div className="grid sm:grid-cols-3 gap-5">
          <F label="Data *">
            <Input data-testid="event-date-input" type="date" value={data.event.date} onChange={(e) => update("event", "date", e.target.value)} />
          </F>
          <F label="Ora inizio">
            <Input data-testid="event-start-input" type="time" value={data.event.time_start} onChange={(e) => update("event", "time_start", e.target.value)} />
          </F>
          <F label="Vuoi più tempo?">
            <select data-testid="extra-hours-select" value={data.event.extra_hours || 0} onChange={(e) => update("event", "extra_hours", Number(e.target.value))}
              className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option value={0}>No, 3 ore mi bastano</option>
              <option value={1}>{`+1 ora (+${config.tariffa_extra_ora || 50} €)`}</option>
              <option value={2}>{`+2 ore (+${(config.tariffa_extra_ora || 50) * 2} €)`}</option>
              <option value={3}>{`+3 ore (+${(config.tariffa_extra_ora || 50) * 3} €)`}</option>
            </select>
          </F>
        </div>
        <div data-testid="overtime-notice" className={`rounded-xl border px-4 py-3 text-sm ${extraHours > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-[#FAF7F2] border-[#e5ddd0] text-[#8a7a6d]"}`}>
          <div className="font-bold text-[#2C1A11] mb-1">
            🕐 Il tuo evento: dalle {data.event.time_start} alle {data.event.time_end}
          </div>
          {extraHours > 0 ? (
            <>Evento standard di <b>{config.durata_standard_ore || 3} ore incluse</b> + <b>{extraHours} {extraHours === 1 ? "ora extra" : "ore extra"}</b> a {config.tariffa_extra_ora} €/ora = <b>+{config.tariffa_extra_ora * extraHours} €</b> già aggiunti al totale.</>
          ) : (
            <>L'evento standard dura <b>{config.durata_standard_ore || 3} ore</b>, già incluse nel prezzo. Se ti serve più tempo, aggiungilo dal menù qui sopra: ogni ora extra costa <b>{config.tariffa_extra_ora} €</b> e viene inserita automaticamente nel preventivo.</>
          )}
        </div>
      </Card>
    );

  const salaExtra = (data.sala.rooms === "entrambe" && data.sala.excl_interna && data.sala.excl_deorsi)
    ? (config.prezzo_excl_entrambe || 1300)
    : (data.sala.excl_interna && data.sala.rooms !== "deorsi" ? (config.prezzo_excl_interna || 500) : 0) +
      (data.sala.excl_deorsi && data.sala.rooms !== "interna" ? (config.prezzo_excl_deorsi || 800) : 0);
  const capInterna = config.capienza_interna || 60;
  const capDeorsi = config.capienza_deorsi || 40;
  const capMax = data.sala.rooms === "interna" ? capInterna : data.sala.rooms === "deorsi" ? capDeorsi : capInterna + capDeorsi;
  const capName = data.sala.rooms === "interna" ? "della Sala Interna" : data.sala.rooms === "deorsi" ? "della Sala Dehors" : "delle due sale insieme";
  const overCapacity = guestsCount > capMax;
  const setSala = (patch) => update("sala", null, patch);
  const setAssignment = (group, room) =>
    setSala({ assignment: { ...(data.sala.assignment || {}), [group]: room } });

  return (
    <Card title="I tuoi ospiti e la sala" subtitle="Quante persone parteciperanno e dove vuoi festeggiare">
      <div className="grid sm:grid-cols-3 gap-5">
        <F label="Adulti">
          <Input data-testid="guests-adults-input" type="number" min="0" value={data.guests.adults} onChange={(e) => update("guests", "adults", Number(e.target.value))} />
        </F>
        <F label="Ragazzi">
          <Input data-testid="guests-teens-input" type="number" min="0" value={data.guests.teens || 0} onChange={(e) => update("guests", "teens", Number(e.target.value))} />
        </F>
        <F label="Bambini">
          <Input data-testid="guests-children-input" type="number" min="0" value={data.guests.children} onChange={(e) => update("guests", "children", Number(e.target.value))} />
        </F>
      </div>
      <div className="text-sm text-[#8a7a6d]">Totale ospiti: <b className="text-[#2C1A11]">{guestsCount}</b></div>
      {animMandatory && (
        <div data-testid="anim-mandatory-notice" className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          ⚠️ Con più di 6 bambini l'<b>Animazione Bambini è obbligatoria</b> (+{animPrice} €): è stata aggiunta automaticamente al preventivo.
        </div>
      )}
      <F label="Esigenze alimentari (allergie, intolleranze, vegani...)">
        <Textarea data-testid="guests-dietary-input" value={data.guests.dietary} onChange={(e) => update("guests", "dietary", e.target.value)} placeholder="Es. 2 celiaci, 1 allergia frutta secca" />
      </F>

      <F label="Dove vuoi festeggiare? *">
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            ["interna", "Sala Interna", `fino a ${capInterna} posti`],
            ["deorsi", "Sala Dehors", `fino a ${capDeorsi} posti`],
            ["entrambe", "Entrambe le sale", `fino a ${capInterna + capDeorsi} posti`],
          ].map(([id, label, sub]) => (
            <button key={id} data-testid={`sala-rooms-${id}`} onClick={() => setSala({ rooms: id })}
              className={`rounded-2xl border p-4 text-left transition-all ${data.sala.rooms === id ? "border-[#C85A32] ring-2 ring-[#C85A32] bg-[#C85A32]/5" : "bg-white hover:border-[#C85A32]/50"}`}>
              <span className={`font-semibold text-sm block ${data.sala.rooms === id ? "text-[#C85A32]" : "text-[#2C1A11]"}`}>{label}</span>
              <span className="text-xs text-[#8a7a6d]">{sub}</span>
            </button>
          ))}
        </div>
      </F>

      {overCapacity && (
        <div data-testid="capacity-warning" className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          ⚠️ <b>Attenzione:</b> {guestsCount} ospiti superano la capienza massima {capName} ({capMax} posti). Puoi comunque inviare la richiesta: valuteremo insieme la soluzione migliore.
        </div>
      )}

      <F label="Vuoi l'uso esclusivo?">
        <div className="space-y-2">
          {(data.sala.rooms === "interna" || data.sala.rooms === "entrambe") && (
            <button data-testid="sala-excl-interna" onClick={() => setSala({ excl_interna: !data.sala.excl_interna })}
              className={`w-full flex items-center justify-between gap-3 rounded-xl border p-4 text-left transition-colors ${data.sala.excl_interna ? "border-[#C85A32] ring-1 ring-[#C85A32] bg-[#C85A32]/5" : "bg-white hover:border-[#C85A32]/50"}`}>
              <span>
                <span className="font-semibold text-sm text-[#2C1A11] block">Sala Interna in uso esclusivo</span>
                <span className="text-xs text-[#8a7a6d]">la sala interna riservata solo al tuo gruppo</span>
              </span>
              <span className="font-mono-num font-bold text-sm text-[#C85A32] shrink-0">+{config.prezzo_excl_interna || 500} €</span>
            </button>
          )}
          {(data.sala.rooms === "deorsi" || data.sala.rooms === "entrambe") && (
            <button data-testid="sala-excl-deorsi" onClick={() => setSala({ excl_deorsi: !data.sala.excl_deorsi })}
              className={`w-full flex items-center justify-between gap-3 rounded-xl border p-4 text-left transition-colors ${data.sala.excl_deorsi ? "border-[#C85A32] ring-1 ring-[#C85A32] bg-[#C85A32]/5" : "bg-white hover:border-[#C85A32]/50"}`}>
              <span>
                <span className="font-semibold text-sm text-[#2C1A11] block">Sala Dehors in uso esclusivo</span>
                <span className="text-xs text-[#8a7a6d]">il dehors riservato solo al tuo gruppo</span>
              </span>
              <span className="font-mono-num font-bold text-sm text-[#C85A32] shrink-0">+{config.prezzo_excl_deorsi || 800} €</span>
            </button>
          )}
          <div data-testid="sala-recap" className={`rounded-xl border px-4 py-3 text-sm ${salaExtra > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-[#FAF7F2] border-[#e5ddd0] text-[#8a7a6d]"}`}>
            {salaExtra > 0 ? (
              data.sala.rooms === "entrambe" && data.sala.excl_interna && data.sala.excl_deorsi ? (
                <>🏛 <b>Entrambe le sale in uso esclusivo: +{config.prezzo_excl_entrambe || 1300} €</b> — tutto il locale riservato a te (già aggiunto al totale).</>
              ) : (
                <>🏛 Esclusività selezionata: <b>+{salaExtra} €</b> già aggiunti al totale. Le sale non in esclusiva restano condivise con la normale clientela.</>
              )
            ) : (
              <>ℹ️ Senza esclusiva la sala scelta ospita il tuo evento insieme alla normale attività del locale, <b>senza costi aggiuntivi</b>.</>
            )}
          </div>
        </div>
      </F>

      {data.sala.rooms === "entrambe" && (
        <div data-testid="sala-assignment-box" className="rounded-xl border border-[#e5ddd0] bg-[#FAF7F2] p-4 space-y-3">
          <div className="text-sm font-semibold text-[#2C1A11]">Come vuoi distribuire gli ospiti nelle due sale?</div>
          {[["adulti", "Adulti"], ["ragazzi", "Ragazzi"], ["bambini", "Bambini"]].map(([k, label]) => (
            <div key={k} className="flex items-center gap-3">
              <span className="text-sm w-24">{label}</span>
              <select data-testid={`assignment-${k}`} value={(data.sala.assignment || {})[k] || "interna"} onChange={(e) => setAssignment(k, e.target.value)}
                className="flex-1 h-9 rounded-md border border-input bg-white px-2 text-sm">
                <option value="interna">Sala Interna</option>
                <option value="deorsi">Sala Dehors</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
